import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TermsOfUse from '../components/ui/TermsOfUse';
import { DotScreenShader } from '../components/DotScreenShader';
import { Footer } from '../components/Footer';
import { CookieConsent } from '../components/CookieConsent';
import { Navbar } from '../components/Navbar';
import { ToolShowcaseCarousel } from '../components/ToolShowcaseCarousel';
import { BentoGrid } from '../components/BentoGrid';

import { BottomCTA } from '../components/BottomCTA';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const LandingPage = ({
    customTitle = '',
    customSubtitle = 'Download From All Your Favorite Platforms',
    customPlaceholderVideo = 'Paste a Video Link (YouTube, Instagram, TikTok + 1700+ Sites)',
    customPlaceholderAudio = 'Paste an Audio Link (Bandcamp, SoundCloud, Spotify, YouTube Music)'
}) => {
    const navigate = useNavigate();
    const [url, setUrl] = useState('');
    const [downloadType, setDownloadType] = useState('video');
    const [videoInfo, setVideoInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showTerms, setShowTerms] = useState(false);
    const lastSearchedUrl = useRef('');

    useEffect(() => {
        if (!url) return;
        const isYouTubeUrl = /^(https?:\/\/(www\.)?youtube\.com\/|https?:\/\/(www\.)?youtu\.be\/)/i.test(url);
        const isInstagramUrl = /^(https?:\/\/(www\.)?instagram\.com\/)/i.test(url);
        if (!isYouTubeUrl && !isInstagramUrl) return;

        const t = setTimeout(() => {
            if (url && url !== lastSearchedUrl.current && !loading) {
                lastSearchedUrl.current = url;
                handleSearch();
            }
        }, 500);
        return () => clearTimeout(t);
    }, [url, loading]);

    const handleSearch = async () => {
        if (!url) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/metadata`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
            });
            if (!res.ok) throw new Error(await res.text());
            const json = await res.json();
            setVideoInfo(json);
        } catch (e) {
            setError(e.message || 'Failed to fetch metadata');
            setVideoInfo(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="App" style={{ position: 'relative' }}>
            <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
                <DotScreenShader theme="dark" />
            </div>

            <Navbar />

            <main className="hero" style={{ position: 'relative', zIndex: 1, paddingTop: '100px', minHeight: 'auto', paddingBottom: '20px' }}>
                <p className="hero-preheader">#1 AI Faceless Video Tool</p>
                <h1 className="hero-heading">Faceless videos made effortless with AI</h1>

                <div className="hero-input-row" style={{ marginBottom: 0 }}>
                    <div className="download-form hero-search">
                        <input
                            type="text"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={downloadType === 'video'
                                ? customPlaceholderVideo
                                : customPlaceholderAudio}
                        />
                        <button onClick={handleSearch} disabled={loading || !url}>{loading ? 'Searching...' : 'Search'}</button>
                    </div>
                    <span className="hero-or-separator">Or</span>
                    <Link to="/signup" className="hero-upload-btn">
                        Upload
                    </Link>
                </div>

                {error && <p className="error">{error}</p>}
                {videoInfo && (
                    <div className="preview-card">
                        <div
                            className="preview-media"
                            style={
                                videoInfo.width && videoInfo.height
                                    ? { aspectRatio: `${videoInfo.width} / ${videoInfo.height}` }
                                    : undefined
                            }
                        >
                            {videoInfo.thumbnail && <img src={videoInfo.thumbnail} alt="thumb" />}
                        </div>
                        <div className="preview-meta">
                            <h2>{videoInfo.title}</h2>
                            <Link to="/signup" style={{ marginTop: 12, padding: '8px 16px', background: '#06b6d4', color: '#000', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-block', textDecoration: 'none' }}>Get Started</Link>
                        </div>
                    </div>
                )}

            </main>

            {/* Tool Showcase Carousel - Edge to Edge */}
            <div style={{ width: '100%', position: 'relative', zIndex: 2 }}>
                <ToolShowcaseCarousel />
            </div>

            {/* Bento Grid Section */}
            <BentoGrid />

            {/* Bottom CTA Section */}
            <BottomCTA />

            <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
                <Footer />
            </div>

            <CookieConsent />

            {showTerms && (
                <div role="dialog" aria-modal="true" onClick={() => setShowTerms(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', color: '#000', width: '100%', maxWidth: 980, maxHeight: '90vh', borderRadius: 8, overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 12 }}>
                            <button onClick={() => setShowTerms(false)} style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Close</button>
                        </div>
                        <TermsOfUse />
                    </div>
                </div>
            )}
        </div>
    );
};
